/**
 * Client helpers for subscription job limits.
 *
 * The database (enforce_job_limit trigger + plan_entitlements) is authoritative.
 * These helpers only interpret its responses for display.
 */

export const JOB_LIMIT_ERROR = "job_limit_reached";
export const DEFAULT_WARNING_THRESHOLD = 0.8;

/** True when a Supabase/PostgREST error is the server's job-limit rejection. */
export function isJobLimitReachedError(err: unknown): boolean {
  if (!err || typeof err !== "object") return typeof err === "string" && err.includes(JOB_LIMIT_ERROR);
  const e = err as { message?: unknown; details?: unknown; hint?: unknown };
  return [e.message, e.details].some((v) => typeof v === "string" && v.includes(JOB_LIMIT_ERROR));
}

export interface ServerJobUsage {
  tier: string;
  plan: string;
  job_limit: number | null;
  unlimited: boolean;
  warning_threshold: number;
  used: number;
  remaining: number | null;
  period_start: string | null;
  period_end: string | null;
  period_source: string;
  is_demo: boolean;
}

/** Validates the get_my_job_usage() payload; returns null when unusable. */
export function parseServerJobUsage(raw: unknown): ServerJobUsage | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.used !== "number" || typeof r.tier !== "string") return null;
  return {
    tier: r.tier,
    plan: typeof r.plan === "string" ? r.plan : r.tier,
    job_limit: typeof r.job_limit === "number" ? r.job_limit : null,
    unlimited: r.unlimited === true || r.job_limit == null,
    warning_threshold: typeof r.warning_threshold === "number" ? r.warning_threshold : DEFAULT_WARNING_THRESHOLD,
    used: r.used,
    remaining: typeof r.remaining === "number" ? r.remaining : null,
    period_start: typeof r.period_start === "string" ? r.period_start : null,
    period_end: typeof r.period_end === "string" ? r.period_end : null,
    period_source: typeof r.period_source === "string" ? r.period_source : "unknown",
    is_demo: r.is_demo === true,
  };
}

export type UsageWarningLevel = "none" | "soft" | "strong" | "critical";

export function computeUsageLevel(used: number, limit: number | null, threshold = DEFAULT_WARNING_THRESHOLD) {
  if (limit === null) {
    return { isAtLimit: false, isNear95: false, isNearLimit: false, percentageUsed: 0, remaining: null as number | null, warningLevel: "none" as UsageWarningLevel };
  }
  const isAtLimit = used >= limit;
  const isNear95 = !isAtLimit && used >= limit * 0.95;
  const isNearLimit = used >= limit * threshold;
  const percentageUsed = Math.min((used / limit) * 100, 100);
  const warningLevel: UsageWarningLevel = isAtLimit ? "critical" : isNear95 ? "strong" : isNearLimit ? "soft" : "none";
  return { isAtLimit, isNear95, isNearLimit, percentageUsed, remaining: Math.max(limit - used, 0), warningLevel };
}

/** localStorage key so the 80% popup shows once per company per billing period. */
export function warningSeenKey(companyId: string, periodStart: string | null): string {
  return `tm_job_limit_warning_seen:${companyId}:${periodStart ?? "unknown"}`;
}
